<template>
  <div ref="tui-editor" :id="id" />
</template>

<script>
import Editor from '@toast-ui/editor'
import defaultOptions from './default-options'
import '@toast-ui/editor/dist/toastui-editor.css' // Editor's Style

const HTML_LINK_TOOLBAR_ITEM_INDEX = {
  groupIndex: 3,
  itemIndex: 2,
}

const escapeHtml = (value = '') =>
  `${value}`
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const isSafeHtmlLinkHref = (value = '') => {
  try {
    const parsed = new URL(`${value}`.trim(), window.location.origin)
    return ['http:', 'https:', 'mailto:', 'tel:'].includes(parsed.protocol)
  } catch (error) {
    return false
  }
}

export default {
  name: 'MarkdownEditor',
  props: {
    value: {
      type: String,
      default: '',
    },
    id: {
      type: String,
      required: false,
      default: `markdown-editor-${new Date()}${(
        Math.random() * 1000
      ).toFixed()}`,
    },
    options: {
      type: Object,
      default() {
        return defaultOptions
      },
    },
    mode: {
      type: String,
      default: 'markdown',
    },
    height: {
      type: String,
      required: false,
      default: '300px',
    },
    language: {
      type: String,
      required: false,
      default: 'en_US', // https://github.com/nhn/tui.editor/blob/master/docs/en/i18n.md#supported-languages
    },
    linkInsertMode: {
      type: String,
      required: false,
      default: 'markdown',
    },
  },
  data() {
    return {
      editor: null,
      localValue: '',
    }
  },
  computed: {
    editorOptions() {
      const options = Object.assign({}, defaultOptions, this.options)
      options.initialEditType = this.mode
      options.height = this.height
      options.language = this.language
      return options
    },
  },
  watch: {
    value(newValue) {
      if (this.editor && this.getMarkdown() !== newValue) {
        this.editor.setMarkdown(newValue)
      }
    },
    id() {
      this.destroyEditor()
      this.initEditor()
    },
    language() {
      this.destroyEditor()
      this.initEditor()
    },
    height(newValue) {
      this.editor.height(newValue)
    },
    mode(newValue) {
      this.editor.changeMode(newValue)
    },
  },
  mounted() {
    this.initEditor()
  },
  destroyed() {
    this.destroyEditor()
  },
  methods: {
    initEditor() {
      this.editor = new Editor({
        el: this.$refs['tui-editor'],
        ...this.editorOptions,
      })

      if (this.linkInsertMode === 'html') {
        this.installHtmlLinkToolbar()
      }

      if (this.value) this.setMarkdown(this.value)

      this.editor.on('change', () => {
        this.localValue = this.getMarkdown()
      })
      this.editor.on('blur', () => {
        this.$emit('input', this.localValue)
      })
    },
    destroyEditor() {
      if (!this.editor) return
      this.editor.destroy()
    },
    installHtmlLinkToolbar() {
      if (!this.editor) return

      this.editor.addCommand('markdown', 'insertHtmlLink', (payload, state, dispatch) => {
        const rawHref = (payload && payload.href) || ''
        const trimmedHref = `${rawHref}`.trim()
        if (!trimmedHref || !isSafeHtmlLinkHref(trimmedHref)) {
          return true
        }

        const href = escapeHtml(trimmedHref)
        const text = escapeHtml((payload && payload.text) || '')
        const targetBlank = Boolean(payload && payload.targetBlank)
        const rel = targetBlank ? ' rel="noopener noreferrer"' : ''
        const target = targetBlank ? ' target="_blank"' : ''
        const html = `<a href="${href}"${target}${rel}>${text}</a>`

        if (dispatch) {
          dispatch(state.tr.insertText(html))
        }
        return true
      })

      this.editor.removeToolbarItem('link')
      this.editor.insertToolbarItem(
        HTML_LINK_TOOLBAR_ITEM_INDEX,
        this.buildHtmlLinkToolbarItem()
      )
    },
    closeToolbarPopup() {
      document.body.dispatchEvent(
        new MouseEvent('mousedown', {
          bubbles: true,
          cancelable: true,
          view: window,
        })
      )
    },
    buildHtmlLinkToolbarItem() {
      const button = document.createElement('button')
      const popupBody = document.createElement('div')
      const title = document.createElement('div')
      const urlLabel = document.createElement('label')
      const urlInput = document.createElement('input')
      const textLabel = document.createElement('label')
      const textInput = document.createElement('input')
      const targetRow = document.createElement('label')
      const targetToggle = document.createElement('input')
      const targetText = document.createElement('span')
      const actions = document.createElement('div')
      const cancelButton = document.createElement('button')
      const insertButton = document.createElement('button')

      button.type = 'button'
      button.className = 'toastui-editor-toolbar-icons link'
      button.setAttribute('aria-label', 'Insert link')
      button.title = 'Insert link'

      title.textContent = 'Insert link'
      title.style.marginBottom = '12px'
      title.style.fontWeight = '600'

      urlLabel.textContent = 'URL'
      urlLabel.style.display = 'block'
      urlLabel.style.marginBottom = '4px'
      urlInput.type = 'text'
      urlInput.style.width = '100%'
      urlInput.style.marginBottom = '12px'

      textLabel.textContent = 'Link text'
      textLabel.style.display = 'block'
      textLabel.style.marginBottom = '4px'
      textInput.type = 'text'
      textInput.style.width = '100%'
      textInput.style.marginBottom = '12px'

      targetRow.style.display = 'flex'
      targetRow.style.alignItems = 'center'
      targetRow.style.gap = '8px'
      targetRow.style.marginBottom = '16px'
      targetToggle.type = 'checkbox'
      targetToggle.checked = true
      targetText.textContent = 'Open in new tab'
      targetRow.appendChild(targetToggle)
      targetRow.appendChild(targetText)

      actions.style.display = 'flex'
      actions.style.justifyContent = 'flex-end'
      actions.style.gap = '8px'

      cancelButton.type = 'button'
      cancelButton.textContent = 'Cancel'
      cancelButton.style.padding = '6px 10px'
      cancelButton.style.border = '1px solid #d8d8d8'
      cancelButton.style.borderRadius = '4px'
      cancelButton.style.background = '#fff'

      insertButton.type = 'button'
      insertButton.textContent = 'Insert'
      insertButton.style.padding = '6px 10px'
      insertButton.style.border = '1px solid #2d8cf0'
      insertButton.style.borderRadius = '4px'
      insertButton.style.background = '#2d8cf0'
      insertButton.style.color = '#fff'

      const focusUrlInput = () => {
        setTimeout(() => {
          urlInput.focus()
        }, 0)
      }

      button.addEventListener('click', () => {
        urlInput.value = ''
        textInput.value = this.editor ? this.editor.getSelectedText() : ''
        targetToggle.checked = true
        focusUrlInput()
      })

      const submit = () => {
        const href = urlInput.value.trim()
        if (!href) return

        const text = textInput.value.trim() || href
        this.editor.exec('insertHtmlLink', {
          href,
          text,
          targetBlank: targetToggle.checked,
        })
        this.closeToolbarPopup()
      }

      cancelButton.addEventListener('click', () => {
        this.closeToolbarPopup()
      })
      insertButton.addEventListener('click', submit)
      textInput.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault()
          submit()
        }
      })
      urlInput.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault()
          submit()
        }
      })

      actions.appendChild(cancelButton)
      actions.appendChild(insertButton)
      popupBody.appendChild(title)
      popupBody.appendChild(urlLabel)
      popupBody.appendChild(urlInput)
      popupBody.appendChild(textLabel)
      popupBody.appendChild(textInput)
      popupBody.appendChild(targetRow)
      popupBody.appendChild(actions)

      return {
        name: 'htmlLink',
        el: button,
        onMounted: () => {
          const toolbarItemWrapper = button.parentElement
          if (toolbarItemWrapper) {
            toolbarItemWrapper.style.margin = '0'
          }
        },
        popup: {
          body: popupBody,
        },
      }
    },
    setMarkdown(value, cursorToEnd = false) {
      this.localValue = value
      this.editor.setMarkdown(value, cursorToEnd)
    },
    getMarkdown() {
      return this.editor.getMarkdown()
    },
    setHtml(value) {
      this.editor.setHtml(value)
    },
    getHtml() {
      return this.editor.getHtml()
    },
  },
}
</script>
